import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Star, Clock, X, MessageSquare, Building, Award, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MyBookingsPage() {
  const [bookings, setBookings] = useState({ facilities: [], coaches: [] });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [cancelModal, setCancelModal] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [review, setReview] = useState({ rating: 5, comment: '' });

  useEffect(() => {
    fetchBookings();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      if (!token) return;

      const response = await axios.get(`${API}/bookings/my-bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) return;

      const response = await axios.get(`${API}/bookings/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleCancelBooking = async (booking) => {
    try {
      const token = localStorage.getItem('session_token');
      const endpoint = booking.booking_type_label === 'facility' 
        ? `/bookings/facility/${booking.booking_id}/cancel`
        : `/bookings/coach/${booking.booking_id}/cancel`;

      const response = await axios.put(
        `${API}${endpoint}`,
        { reason: cancelReason },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert(`Booking cancelled. Refund: ₹${response.data.refund_amount.toFixed(2)}`);
      setCancelModal(null);
      setCancelReason('');
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleSubmitReview = async (booking) => {
    try {
      const token = localStorage.getItem('session_token');
      const endpoint = booking.booking_type_label === 'facility'
        ? `/bookings/facility/${booking.booking_id}/review`
        : `/synergies/coaches/${booking.coach_id}/review`;

      await axios.post(
        `${API}${endpoint}`,
        { ...review, booking_id: booking.booking_id },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert('Review submitted successfully!');
      setReviewModal(null);
      setReview({ rating: 5, comment: '' });
      fetchBookings();
    } catch (error) {
      console.error('Error submitting review:', error);
      alert(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const allBookings = [...bookings.facilities, ...bookings.coaches].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const filteredBookings = allBookings.filter(booking => {
    if (activeTab === 'all') return true;
    if (activeTab === 'upcoming') return booking.booking_status === 'confirmed';
    if (activeTab === 'completed') return booking.booking_status === 'completed';
    if (activeTab === 'cancelled') return booking.booking_status === 'cancelled';
    return true;
  });

  const BookingCard = ({ booking }) => {
    const isFacility = booking.booking_type_label === 'facility';
    const details = isFacility ? booking.facility_details : booking.coach_details;
    const canCancel = booking.booking_status === 'confirmed';
    const canReview = booking.booking_status === 'completed';

    return (
      <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Image */}
          <div className="w-full md:w-32 h-32 rounded-lg overflow-hidden bg-deep-dark flex-shrink-0">
            {details && (isFacility ? details.photos?.[0] : details.photo) ? (
              <img 
                src={isFacility ? details.photos[0] : details.photo} 
                alt={details.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {isFacility ? <Building className="w-12 h-12 text-white/20" /> : <Award className="w-12 h-12 text-white/20" strokeWidth={1.5} />}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className={`inline-block px-2 py-1 text-xs font-bold rounded mb-2 ${
                  booking.booking_status === 'confirmed' ? 'bg-premium-gold text-dark-purple' :
                  booking.booking_status === 'completed' ? 'bg-green-600 text-white' :
                  'bg-gray-600 text-white'
                }`}>
                  {booking.booking_status}
                </span>
                <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Fraunces, serif' }}>
                  {details?.name || 'Unknown'}
                </h3>
                <div className="flex items-center gap-2 text-white/60 text-sm">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                  <span>{details?.city}, {details?.country}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  ₹{booking.final_amount.toLocaleString()}
                </div>
                <div className="text-xs text-white/50">{booking.currency}</div>
              </div>
            </div>

            {/* Booking Info */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Calendar className="w-4 h-4" strokeWidth={1.5} />
                <span>
                  {new Date(isFacility ? booking.start_datetime : booking.session_date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Clock className="w-4 h-4" strokeWidth={1.5} />
                <span>
                  {isFacility 
                    ? `${booking.duration_hours}h - ${booking.booking_type}` 
                    : `${booking.session_duration_hours}h session`
                  }
                </span>
              </div>
            </div>

            {/* Sport/Type */}
            {booking.sport && (
              <div className="mb-4">
                <span className="px-3 py-1 bg-deep-dark text-white/70 text-sm rounded capitalize">
                  {booking.sport}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Link 
                to={isFacility ? `/synergies/facilities/${booking.facility_id}` : `/synergies/coaches/${booking.coach_id}`}
                className="px-4 py-2 bg-deep-dark hover:bg-deep-dark text-white text-sm rounded-lg transition-colors"
              >
                View Details
              </Link>
              {canCancel && (
                <button
                  onClick={() => setCancelModal(booking)}
                  className="px-4 py-2 bg-premium-gold hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel Booking
                </button>
              )}
              {canReview && (
                <button
                  onClick={() => setReviewModal(booking)}
                  className="px-4 py-2 bg-premium-gold hover:bg-yellow-600 text-dark-purple text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <Star className="w-4 h-4" strokeWidth={1.5} />
                  Write Review
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-dark py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-6 sm:mb-8 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
          My <span className="gold-gradient">Bookings</span>
        </h1>

        {/* Analytics */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
              <div className="text-white/60 text-sm mb-1">Total Bookings</div>
              <div className="text-3xl font-bold text-white">{analytics.overall.total_bookings}</div>
            </div>
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
              <div className="text-white/60 text-sm mb-1">Facilities</div>
              <div className="text-3xl font-bold text-white">{analytics.facilities.total}</div>
            </div>
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
              <div className="text-white/60 text-sm mb-1">Coaches</div>
              <div className="text-3xl font-bold text-white">{analytics.coaches.total}</div>
            </div>
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/50">
              <div className="text-white/60 text-sm mb-1">Total Spent</div>
              <div className="text-3xl font-bold text-premium-gold">₹{analytics.overall.total_spent.toLocaleString()}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'upcoming', 'completed', 'cancelled'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 sm:px-6 py-3 min-h-[44px] rounded-lg font-semibold text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-premium-gold text-white'
                  : 'bg-dark-surface text-white/60 hover:text-white border border-premium-gold/30/50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          {filteredBookings.length === 0 ? (
            <div className="text-center py-16 bg-dark-surface rounded-xl border border-premium-gold/30/50">
              <Calendar className="w-16 h-16 text-white/30 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-xl text-white/60">No bookings found</p>
            </div>
          ) : (
            filteredBookings.map(booking => (
              <BookingCard key={booking.booking_id} booking={booking} />
            ))
          )}
        </div>

        {/* Cancel Modal */}
        {cancelModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-dark-surface rounded-xl p-8 max-w-md w-full border border-premium-gold/30/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-premium-gold/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-premium-gold" />
                </div>
                <h3 className="text-2xl font-bold text-white">Cancel Booking?</h3>
              </div>
              <p className="text-white/70 mb-4">
                Cancellation fees may apply based on the timing. Please provide a reason:
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:outline-none mb-4"
                rows="3"
                placeholder="Reason for cancellation..."
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setCancelModal(null)}
                  className="flex-1 px-4 py-3 bg-deep-dark hover:bg-deep-dark text-white rounded-lg transition-colors"
                >
                  Keep Booking
                </button>
                <button
                  onClick={() => handleCancelBooking(cancelModal)}
                  className="flex-1 px-4 py-3 bg-premium-gold hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Confirm Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review Modal */}
        {reviewModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-dark-surface rounded-xl p-8 max-w-md w-full border border-premium-gold/30/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-premium-gold/20 rounded-full flex items-center justify-center">
                  <Star className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-bold text-white">Write a Review</h3>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold text-white/70 mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReview({ ...review, rating: star })}
                      className="transition-colors"
                    >
                      <Star 
                        className={`w-8 h-8 ${star <= review.rating ? 'text-premium-gold fill-current' : 'text-white/30'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-white/70 mb-2">Your Review</label>
                <textarea
                  value={review.comment}
                  onChange={(e) => setReview({ ...review, comment: e.target.value })}
                  className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  rows="4"
                  placeholder="Share your experience..."
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setReviewModal(null)}
                  className="flex-1 px-4 py-3 bg-deep-dark hover:bg-deep-dark text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSubmitReview(reviewModal)}
                  className="flex-1 px-4 py-3 bg-premium-gold hover:bg-yellow-600 text-dark-purple font-bold rounded-lg transition-colors"
                >
                  Submit Review
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyBookingsPage;
